import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { ArrowBack as ArrowBackIcon } from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import TenantAdminJobProgressDialog from '../../components/sistema/TenantAdminJobProgressDialog';
import tenantAdminService from '../../services/tenantAdminService';
import { TenantDataCleanupCatalog, TenantDataCleanupSection } from '../../types';
import { getApiErrorMessage } from '../../utils/apiError';

const formatCount = (value: number) => value.toLocaleString('es-AR');

const TenantDataCleanup: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { enqueueSnackbar } = useSnackbar();
  const tenantId = Number(id);

  const [catalog, setCatalog] = useState<TenantDataCleanupCatalog | null>(null);
  const [selected, setSelected] = useState<Record<string, boolean>>({});
  const [loading, setLoading] = useState(true);
  const [confirmStep, setConfirmStep] = useState<0 | 1 | 2>(0);
  const [confirmCode, setConfirmCode] = useState('');
  const [jobId, setJobId] = useState<string | null>(null);
  const [starting, setStarting] = useState(false);
  const [cleanupSucceeded, setCleanupSucceeded] = useState(false);

  const loadCatalog = useCallback(async () => {
    if (!Number.isFinite(tenantId) || tenantId <= 0) {
      navigate('/sistema/tenants', { replace: true });
      return;
    }
    try {
      setLoading(true);
      const data = await tenantAdminService.getDataCleanupCatalog(tenantId);
      setCatalog(data);
      const next: Record<string, boolean> = {};
      data.sections.forEach((section) => {
        section.items.forEach((item) => {
          next[item.key] = item.defaultSelected;
        });
      });
      setSelected(next);
    } catch (error: unknown) {
      enqueueSnackbar(getApiErrorMessage(error, 'No se pudo cargar el catálogo de limpieza'), {
        variant: 'error',
      });
      navigate('/sistema/tenants');
    } finally {
      setLoading(false);
    }
  }, [tenantId, enqueueSnackbar, navigate]);

  useEffect(() => {
    void loadCatalog();
  }, [loadCatalog]);

  const selectedItems = useMemo(() => {
    if (!catalog) return [];
    return catalog.sections.flatMap((section) =>
      section.items.filter((item) => selected[item.key]),
    );
  }, [catalog, selected]);

  const selectedCount = selectedItems.reduce((sum, item) => sum + item.count, 0);

  const toggleItem = (key: string) => {
    setSelected((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const setSectionSelected = (section: TenantDataCleanupSection, value: boolean) => {
    setSelected((prev) => {
      const next = { ...prev };
      section.items.forEach((item) => {
        next[item.key] = value;
      });
      return next;
    });
  };

  const handleProcess = () => {
    if (selectedItems.length === 0) {
      enqueueSnackbar('Seleccioná al menos un bloque para procesar.', { variant: 'warning' });
      return;
    }
    setConfirmCode('');
    setConfirmStep(1);
  };

  const handleStartCleanup = async () => {
    if (!catalog) return;
    try {
      setStarting(true);
      const started = await tenantAdminService.startDataCleanup(
        catalog.tenantId,
        selectedItems.map((item) => item.key),
      );
      setConfirmStep(0);
      setJobId(started.jobId);
    } catch (error: unknown) {
      enqueueSnackbar(getApiErrorMessage(error, 'No se pudo iniciar la limpieza'), {
        variant: 'error',
      });
    } finally {
      setStarting(false);
    }
  };

  const loadCleanupJob = useCallback((cleanupJobId: string) => {
    return tenantAdminService.getCleanupJob(cleanupJobId);
  }, []);

  const handleCleanupSucceeded = useCallback(() => {
    setCleanupSucceeded(true);
    enqueueSnackbar('Limpieza de datos completada.', { variant: 'success' });
  }, [enqueueSnackbar]);

  const handleJobClose = () => {
    const goToEdit = cleanupSucceeded && catalog;
    setJobId(null);
    setCleanupSucceeded(false);
    if (goToEdit) {
      navigate(`/sistema/tenants/${catalog.tenantId}`);
    }
  };

  const codeMatches = confirmCode.trim().toLowerCase() === (catalog?.tenantCode ?? '').toLowerCase();

  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, bgcolor: '#f5f5f5', minHeight: '100vh' }}>
      <Card
        sx={{
          bgcolor: '#ffffff',
          borderRadius: 3,
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          overflow: 'hidden',
        }}
      >
        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            alignItems={{ xs: 'flex-start', sm: 'center' }}
            spacing={1.5}
            sx={{ mb: 3, flexWrap: 'wrap', gap: 1 }}
          >
            <Button
              startIcon={<ArrowBackIcon />}
              onClick={() => navigate('/sistema/tenants')}
              sx={{ textTransform: 'none' }}
            >
              Administración de Tenants
            </Button>
            <Typography variant="h4" component="h1" sx={{ fontWeight: 600, color: '#1976d2' }}>
              Limpiar datos heredados
            </Typography>
          </Stack>

          {loading || !catalog ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
              <CircularProgress />
            </Box>
          ) : (
            <Stack spacing={3}>
              <Alert severity="warning">
                Tenant {catalog.tenantCode} — {catalog.tenantName}. El borrado es irreversible.
                Desmarcá lo que quieras conservar. El proceso respeta el orden de integridad referencial.
              </Alert>

              {catalog.sections.map((section) => {
                const allOn = section.items.every((item) => selected[item.key]);
                return (
                  <Card key={section.key} variant="outlined">
                    <CardContent>
                      <Stack
                        direction={{ xs: 'column', sm: 'row' }}
                        justifyContent="space-between"
                        alignItems={{ xs: 'flex-start', sm: 'center' }}
                        spacing={1}
                        sx={{ mb: 1 }}
                      >
                        <Typography variant="h6" sx={{ fontWeight: 600 }}>
                          {section.title}
                        </Typography>
                        <Button
                          size="small"
                          onClick={() => setSectionSelected(section, !allOn)}
                          sx={{ textTransform: 'none' }}
                        >
                          {allOn ? 'Deseleccionar sección' : 'Seleccionar sección'}
                        </Button>
                      </Stack>
                      <Stack>
                        {section.items.map((item) => (
                          <FormControlLabel
                            key={item.key}
                            control={
                              <Checkbox
                                checked={Boolean(selected[item.key])}
                                onChange={() => toggleItem(item.key)}
                              />
                            }
                            label={`${item.label} (${formatCount(item.count)})`}
                          />
                        ))}
                      </Stack>
                    </CardContent>
                  </Card>
                );
              })}

              <Stack direction="row" spacing={2} justifyContent="flex-end">
                <Button variant="outlined" onClick={() => navigate(`/sistema/tenants/${catalog.tenantId}`)}>
                  Cancelar
                </Button>
                <Button
                  variant="contained"
                  color="error"
                  disabled={selectedItems.length === 0}
                  onClick={handleProcess}
                >
                  Procesar
                </Button>
              </Stack>
            </Stack>
          )}
        </CardContent>
      </Card>

      <Dialog open={confirmStep === 1} onClose={() => setConfirmStep(0)} fullWidth maxWidth="sm">
        <DialogTitle>Proceso irreversible</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Se eliminarán los bloques seleccionados. Esta acción no se puede deshacer.
          </Typography>
          <Stack spacing={0.5}>
            {selectedItems.map((item) => (
              <Typography key={item.key} variant="body2">
                {item.label}: {formatCount(item.count)} registros
              </Typography>
            ))}
          </Stack>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            Total estimado: {formatCount(selectedCount)} registros.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmStep(0)}>Volver</Button>
          <Button variant="contained" color="error" onClick={() => setConfirmStep(2)}>
            Continuar
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={confirmStep === 2} onClose={() => !starting && setConfirmStep(0)} fullWidth maxWidth="sm">
        <DialogTitle>Confirmá el código del tenant</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Escribí <strong>{catalog?.tenantCode}</strong> para habilitar la eliminación.
          </Typography>
          <TextField
            autoFocus
            fullWidth
            label="Código del tenant"
            value={confirmCode}
            onChange={(e) => setConfirmCode(e.target.value)}
            disabled={starting}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmStep(0)} disabled={starting}>
            Cancelar
          </Button>
          <Button
            variant="contained"
            color="error"
            disabled={!codeMatches || starting}
            onClick={() => void handleStartCleanup()}
          >
            {starting ? 'Iniciando...' : 'Eliminar'}
          </Button>
        </DialogActions>
      </Dialog>

      <TenantAdminJobProgressDialog
        open={Boolean(jobId)}
        title={`Limpiar datos de ${catalog?.tenantCode ?? ''}`}
        description="Se borran los bloques seleccionados en orden de integridad referencial. No cierre esta ventana hasta que termine."
        jobId={jobId}
        loadJob={loadCleanupJob}
        onClose={handleJobClose}
        onSucceeded={handleCleanupSucceeded}
      />
    </Box>
  );
};

export default TenantDataCleanup;
